import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getUserSettings } from "../utils/api";

const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];

/**
 * Signs the user out after a configurable period of inactivity.
 * The timeout (in minutes) is read from /users/{uid}/settings/preferences.
 * A value of 0 (or missing) disables the feature.
 *
 * Listens to pointer/keyboard/scroll events to reset the timer;
 * passive listeners + a single setTimeout keep overhead minimal.
 */
export default function useIdleLogout() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const minutesRef = useRef(0);

  useEffect(() => {
    if (!currentUser) return;

    let cancelled = false;

    (async () => {
      try {
        const s = await getUserSettings();
        if (!cancelled) minutesRef.current = s?.idleTimeoutMinutes || 0;
        if (!cancelled) reset();
      } catch { /* ignore — default to off */ }
    })();

    function signOutNow() {
      logout().finally(() => navigate("/login"));
    }

    function reset() {
      if (timerRef.current) clearTimeout(timerRef.current);
      const minutes = minutesRef.current;
      if (!minutes || minutes <= 0) return;
      timerRef.current = setTimeout(signOutNow, minutes * 60 * 1000);
    }

    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, reset, { passive: true }));

    // Cross-tab: if another tab updates the setting, the settings page will
    // dispatch a custom event to re-read.
    window.addEventListener("idle-timeout-changed", async () => {
      try {
        const s = await getUserSettings();
        minutesRef.current = s?.idleTimeoutMinutes || 0;
        reset();
      } catch { /* ignore */ }
    });

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, reset));
    };
  }, [currentUser, logout, navigate]);
}
